import { Tooltip } from '@mui/material';
import axios from 'axios';
import cn from 'classnames';
import fileDownload from 'js-file-download';
import React, { createRef, memo, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import logosTaskImage from '../../../assets/img/logos-task-image.svg';

import { useModal, useOnClickOutside } from 'shared/hooks';
import { AudioPlayer, CommentModal, TaskName, VideoPlayer } from 'shared/ui';
import { MaxGrade } from 'shared/ui/MaxGrade/MaxGrade';
import Checkbox from 'Components/Checkbox/Checkbox';

import { StudentPdfInput, StudentWordInput } from '..';
import { commentApi } from '../../../api';
import { imgApi } from '../../../api/img-api';
import checkSvg from '../../../assets/img/check_icon.svg';
import closeSvg from '../../../assets/img/close.svg';
import editSvg from '../../../assets/img/edit.svg';
import imageSvg from '../../../assets/img/image-rectangle.svg';
import paperClipSvg from '../../../assets/img/paper-clip.svg';
import starSvg from '../../../assets/img/star.svg';
import { getUserType } from '../../../redux/slices/userSlice';
import { ICL, TaskComment, TaskKeysType, WorkType } from '../../../shared/types';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import BigPictureModal from '../../BigPictureModal';
import { EditorContext } from '../../ImageEditor/EditorProvider';
import CompleteCheckTask from '../CompleteCheckTask';
import StudentDocumentItem from '../StudentDocumentItem';
import TaskDeadline from '../TaskDeadline';
import { getTotalPoint } from 'redux/slices/flagsSlice';
import InputPoint from 'Components/InputPoint/InputPoint';

interface StudentTaskWrapperProps {
  id: string;
  taskText: string;
  subText?: string;
  link?: string;
  children: React.JSX.Element | React.JSX.Element[];
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | string[] | boolean) => void;
  checkedAt?: string;
  hasPhoto?: boolean;
  answersPhotos?: string[];
  completedAt?: string;
  saveTaskAnswer: (id: string) => Promise<void>;
  icl: ICL | null;
  deadline?: string;
  audioUrl?: string;
  answer?: string;
  hasAnswer?: boolean;
  hasStar?: boolean;
  videoUrl?: string;
  taskPhotos?: string[];
  editedAnswerPhotos?: string[];
  hw?: boolean;
  pdfUrl?: string;
  wordUrl?: string;
  studentPdfUrl?: string;
  studentWordUrl?: string;
  preview?: boolean;
  isDisabledSubmitButton?: boolean;
  isSubmitButton?: boolean;
  taskName?: string;
  areStudentAnswersCorrect?: boolean;
  isExamCompleted?: boolean;
  maxGrade?: number;
  grade?: number;
  studentName?: string;
  index: number;
  points?: number;
  workTaskType?: WorkType;
  teacherPoints?: number;
}

function StudentTaskWrapper({
  id,
  taskText,
  subText,
  link,
  children,
  handleChangeTaskByKey,
  checkedAt,
  hasPhoto,
  answersPhotos,
  completedAt,
  saveTaskAnswer,
  deadline,
  icl,
  audioUrl,
  answer,
  hasAnswer,
  hasStar,
  videoUrl,
  taskPhotos,
  editedAnswerPhotos,
  hw,
  pdfUrl,
  wordUrl,
  studentPdfUrl,
  studentWordUrl,
  preview,
  isDisabledSubmitButton = false,
  isSubmitButton = false,
  taskName,
  areStudentAnswersCorrect,
  isExamCompleted,
  maxGrade,
  grade,
  studentName,
  index,
  points,
  teacherPoints,
  workTaskType,
}: StudentTaskWrapperProps) {
  // points: its max point what student can get for a task
  // teacherPoints: its what actually student get after check
  const isExam = icl?.type === WorkType.exam;

  const { t } = useTranslation();

  console.log(teacherPoints, 'points');

  const { isOpen, close, open } = useModal();
  const { isOpen: isBigPictureOpen, close: bigPictureCLose, open: bigPictureOpen } = useModal();
  const { isOpen: isDeleteImgOpen, open: openDeleteImg, close: closeDeleteImg } = useModal();
  const { isOpen: isDeleteEditImgOpen, open: openDeleteEditImg, close: closeDeleteEditImg } = useModal();
  const { isOpen: isFilesModalOpen, close: closeFilesModal, toggle: toggleFilesModal } = useModal();

  const imgInputRef = createRef<HTMLInputElement>();
  const filesModalRef = useOnClickOutside(closeFilesModal);

  const { setImageForEdit, setTargetTaskId, setEditedImageArray } = useContext(EditorContext);
  const userTypeSelector = useSelector(getUserType);

  const [comments, setComments] = useState<TaskComment[]>();
  const [imageForDelete, setImageForDelete] = useState<string>('');
  const [bigPicture, setBigPicture] = React.useState<string>('');

  useEffect(() => {
    if (!icl || !id) {
      return;
    }

    const setGottenComments = (gottenComments: TaskComment[]) => setComments(gottenComments);
    const unsubscribe = commentApi.subscribeToComments({ iclId: icl.id, taskId: id, setComments: setGottenComments });

    return () => {
      unsubscribe();
    };
  }, [icl?.id]);

  const handleDeleteImage = async (img: string) => {
    try {
      const newPhotos = await imgApi.removeImg(img, icl!.id, id, answersPhotos || [], 'answersPhotos');

      handleChangeTaskByKey(id, 'answersPhotos', newPhotos);
      toast.success(t('Successfully removed!'));
      closeDeleteImg();
      setImageForDelete('');
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
      closeDeleteImg();
      setImageForDelete('');
    }
  };

  const handleDeleteEditedImage = async (img: string) => {
    try {
      const newPhotos = await imgApi.removeImg(img, icl!.id, id, editedAnswerPhotos || [], 'editedAnswerPhotos');

      handleChangeTaskByKey(id, 'editedAnswerPhotos', newPhotos);
      toast.success(t('Successfully removed!'));
      closeDeleteEditImg();
      setImageForDelete('');
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
      closeDeleteEditImg();
      setImageForDelete('');
    }
  };

  const handleSetImageForEdit = (event: React.MouseEvent, photo: string) => {
    event.stopPropagation();

    setImageForEdit(photo);
    setTargetTaskId(id);
    setEditedImageArray(editedAnswerPhotos || []);
  };

  const handleAddImage = async () => {
    const imgInput = imgInputRef.current;

    if (!imgInput || !imgInput.files || !icl) {
      return;
    }

    const file = imgInput.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      const gottenImageUrlArr = await imgApi.addImg(file, icl?.id, id, answersPhotos || [], 'answersPhotos');

      handleChangeTaskByKey(id, 'answersPhotos', gottenImageUrlArr);

      toast.success(t('Successfully added!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }
  };

  const renderImgButton = (photo: string) => {
    if (userTypeSelector !== 'Student') {
      return (
        <button
          className="absolute right-[10px] top-[10px] flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[6px] border-[1px] border-[#d1cec7] bg-white p-[3px]"
          onClick={e => handleSetImageForEdit(e, photo)}
        >
          <img src={editSvg} alt={'edit'} />
        </button>
      );
    } else {
      return (
        <button
          className="absolute right-[10px] top-[10px] cursor-pointer rounded-[6px] border-[1px] border-[#d1cec7] bg-white p-[3px]"
          onClick={e => {
            e.stopPropagation();

            setImageForDelete(photo);

            openDeleteImg();
          }}
        >
          <img src={closeSvg} alt={'close'} />
        </button>
      );
    }
  };

  const renderEditedImgButton = (photo: string) => {
    return (
      userTypeSelector !== 'Student' && (
        <>
          <button
            className="absolute right-[50px] top-[10px] flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[6px] border-[1px] border-[#d1cec7] bg-white p-[3px]"
            onClick={e => handleSetImageForEdit(e, photo)}
          >
            <img src={editSvg} alt={'edit'} />
          </button>

          <button
            className="absolute right-[10px] top-[10px] cursor-pointer rounded-[6px] border-[1px] border-[#d1cec7] bg-white p-[3px]"
            onClick={e => {
              e.stopPropagation();

              setImageForDelete(photo);

              openDeleteEditImg();
            }}
          >
            <img src={closeSvg} alt={'close'} />
          </button>
        </>
      )
    );
  };

  const downloadFile = (fileUrl: string) => {
    const correction = fileUrl.includes('/Pdf') ? 7 : 8; // /Pdf or /Word
    const startingIndex = fileUrl.lastIndexOf('/') + correction;
    const endingIndex = fileUrl.lastIndexOf('?');
    const fileName = fileUrl.slice(startingIndex, endingIndex);

    axios
      .get(fileUrl, {
        responseType: 'blob',
      })
      .then(res => {
        fileDownload(res.data, fileName);
      });
  };

  const showPointForCheck =
    icl?.status !== undefined && icl?.status === 'completed' && isExam && userTypeSelector === 'Teacher';

  return (
    <>
      <div
        className={cn(
          'justify-content-between mt-[15px] flex flex-col rounded-xl border-[1px] pb-[15px]',
          { 'border-[#EBEEF2]': !isExamCompleted },
          { 'border-[#FA6238]': isExamCompleted && !areStudentAnswersCorrect },
          { 'border-[#197A17]': isExamCompleted && areStudentAnswersCorrect },
        )}
      >
        <div className="justify-content-between  flex-column rounded-t-xl bg-[#fdf2f9] px-[16px] py-[15px]">
          <div className="flex items-center justify-between">
            <div
              className="flex items-center"
              style={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
            >
              <div className="flex  items-center">
                <TaskName type={taskName} taskNumber={index + 1} />
                {hasStar && <img src={starSvg} alt={'star'} />}

                <img src={logosTaskImage} alt="logos-task-image" />
              </div>

              <div className="flex-column flex">
                {studentName && (
                  <p style={{ fontWeight: 'bold' }}>
                    {t('Student')}: {`${studentName}`}
                  </p>
                )}

                {points && isExam && (
                  <p style={{ fontWeight: 'bold' }}>
                    {t('pointCount')}
                    {` ${points}`}
                  </p>
                )}

                {teacherPoints && isExam && userTypeSelector === 'Student' && (
                  <p style={{ fontWeight: 'bold' }}>
                    {t('studentPointForCurrentTask')}
                    {` ${teacherPoints}`}
                  </p>
                )}
              </div>
            </div>

            {maxGrade && <MaxGrade grade={grade} maxGrade={maxGrade} />}
          </div>

          {showPointForCheck && (
            <InputPoint
              title={true}
              value={teacherPoints ?? 0}
              onChange={value => handleChangeTaskByKey(id, 'teacherPoints', value)}
            />
          )}
          <div
            className="mt-[10px] text-[18px] font-bold leading-7 lg:text-[22px]"
            dangerouslySetInnerHTML={{ __html: taskText }}
          ></div>

          {subText && (
            <div
              className="mt-[10px] text-[16px] font-semibold leading-7 lg:text-[20px]"
              dangerouslySetInnerHTML={{ __html: subText }}
            ></div>
          )}

          {link && (
            <div className="mt-[10px]">
              <a
                className="text-[18px] font-bold text-[#4383F2] underline lg:text-[22px]"
                href={link}
                target="_blank"
                rel="noreferrer"
              >
                {t('Follow link')}
              </a>
            </div>
          )}

          {taskPhotos?.length ? (
            <div className="relative flex-wrap overflow-x-auto py-[16px]">
              <div className="flex w-full flex-wrap gap-[12px]">
                {taskPhotos.map(it => {
                  return (
                    <div
                      onClick={() => {
                        setBigPicture(it);
                        bigPictureOpen();
                      }}
                      className="сursor-pointer relative h-[120px] w-[120px] overflow-hidden rounded-[10px] border-[2px] border-[#52C7FA] lg:h-[130px] lg:w-[130px]"
                      key={it}
                    >
                      <img className="h-[150px] w-[150px] cursor-pointer" src={it} alt={it} />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {!preview && (
            <div className="flex flex-col gap-[20px]">
              {audioUrl && <AudioPlayer src={audioUrl} />}
              {videoUrl && <VideoPlayer src={videoUrl} />}
            </div>
          )}
        </div>

        <div className="px-[16px]">{children}</div>

        {hasAnswer && (
          <div className="py-[12px]">
            <textarea
              className="h-[300px] w-full border-none bg-[linear-gradient(to_right,_white_16px,_transparent_10px),_linear-gradient(to_left,_white_16px,_transparent_10px),_repeating-linear-gradient(white,_white_33px,_#97A0C3_35px,_#97A0C3_31px,_white_35px)] bg-local px-[16px] pt-[8px] leading-[35px]"
              disabled={Boolean(completedAt) || userTypeSelector === 'Teacher' || preview}
              value={answer}
              onChange={event => handleChangeTaskByKey(id, 'answer', event.target.value)}
            />
          </div>
        )}

        {!preview && (
          <div className="flex flex-col">
            <div className="flex flex-col gap-[16px] px-[16px] lg:flex-row">
              {answersPhotos?.length ? (
                <div className="flex w-full flex-col lg:max-w-[50%]">
                  <div className="sticky mb-[8px] flex gap-[5px] text-[16px] font-bold uppercase lg:mb-[4px] lg:text-[18px]">
                    {t('Added by student')}
                  </div>

                  <div className="relative w-full overflow-x-auto py-[16px] lg:border-r-[1px] lg:border-r-[#EBEEF2]">
                    <div className="flex w-fit gap-[12px]">
                      {answersPhotos.map(it => {
                        return (
                          <div
                            onClick={() => {
                              setBigPicture(it);
                              bigPictureOpen();
                            }}
                            className="relative h-[130px] w-[130px] cursor-pointer overflow-hidden rounded-[10px] border-[2px] border-[#52C7FA]"
                            key={it}
                          >
                            {renderImgButton(it)}
                            <img className="h-[150px] w-[150px]" src={it} alt={it} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {editedAnswerPhotos?.length ? (
                <div className="flex w-full flex-col lg:max-w-[50%]">
                  <div className="sticky mb-[8px] flex gap-[5px] text-[16px] font-bold uppercase lg:mb-[4px] lg:text-[18px]">
                    <img src={checkSvg} alt={'check'} /> {t('Edited by teacher')}
                  </div>

                  <div className="relative w-full overflow-x-auto py-[16px]">
                    <div className="flex w-fit gap-[12px]">
                      {editedAnswerPhotos.map(it => {
                        return (
                          <div
                            onClick={() => {
                              setBigPicture(it);
                              bigPictureOpen();
                            }}
                            className="relative h-[130px] w-[130px] cursor-pointer overflow-hidden rounded-[10px] border-[2px] border-[#52C7FA]"
                            key={it}
                          >
                            {renderEditedImgButton(it)}
                            <img className="h-[150px] w-[150px]" src={it} alt={it} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {icl && icl.type === WorkType.hw && (
              <div className="my-[12px] flex flex-col gap-[12px] px-[16px]">
                {(pdfUrl || wordUrl) && (
                  <div className="flex flex-col gap-[15px]">
                    <div className="font-bold">{t('addByTeacher')}</div>

                    {pdfUrl && (
                      <StudentDocumentItem
                        url={pdfUrl}
                        currentICLId={icl.id}
                        type={'pdf'}
                        id={id}
                        student={false}
                        downloadFile={downloadFile}
                      />
                    )}

                    {wordUrl && (
                      <StudentDocumentItem
                        url={wordUrl}
                        currentICLId={icl.id}
                        type={'doc'}
                        id={id}
                        student={false}
                        downloadFile={downloadFile}
                      />
                    )}
                  </div>
                )}

                {(studentWordUrl || studentPdfUrl) && (
                  <div className="flex flex-col gap-[15px]">
                    <div className="font-bold">{t('addByStudent')}</div>

                    {studentPdfUrl && (
                      <StudentDocumentItem
                        url={studentPdfUrl}
                        currentICLId={icl.id}
                        type={'pdf'}
                        id={id}
                        student
                        downloadFile={downloadFile}
                      />
                    )}

                    {studentWordUrl && (
                      <StudentDocumentItem
                        url={studentWordUrl}
                        currentICLId={icl.id}
                        type={'doc'}
                        id={id}
                        student
                        downloadFile={downloadFile}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-[12px] px-[16px] lg:flex-row lg:justify-between">
              {icl?.status !== 'checked' && (
                <CompleteCheckTask
                  id={id}
                  completedAt={completedAt}
                  checkedAt={checkedAt}
                  handleChangeTaskByKey={handleChangeTaskByKey}
                  icl={icl}
                  textTask={taskText}
                  hw={hw}
                />
              )}

              <div className="flex flex-col items-center gap-[12px] lg:flex-row">
                {userTypeSelector === 'Student' && (
                  <div ref={filesModalRef} className="relative w-full lg:hidden">
                    {(hasPhoto || hw) && (
                      <button
                        className="btn more flex h-[43px] w-full items-center gap-[10px] bg-[#FAF8F4] !text-[16px] lg:max-w-[240px] lg:!text-[18px]"
                        onClick={toggleFilesModal}
                      >
                        <img src={paperClipSvg} alt="image" />

                        {t('add file')}
                      </button>
                    )}

                    <div
                      className={`absolute bottom-12 z-50 flex w-[275px] flex-col gap-[20px] rounded-[10px] border-2 border-[#52C7FA] bg-white p-[30px] duration-300 ${isFilesModalOpen ? 'opacity-100' : 'invisible opacity-0'}`}
                    >
                      {hasPhoto && userTypeSelector === 'Student' && (
                        <button
                          className="flex items-center gap-[10px] rounded-[8px] !p-[0px]"
                          onClick={() => {
                            imgInputRef.current?.click();
                          }}
                        >
                          <img src={imageSvg} alt="image" />

                          <p className="!mb-0 text-base font-bold text-[#242C2C]">{t('add-photo')}</p>

                          <input
                            className="hidden"
                            id="imageInput"
                            ref={imgInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleAddImage}
                          />
                        </button>
                      )}

                      {icl && icl.type === WorkType.hw && (
                        <>
                          <StudentPdfInput
                            currentICLId={icl.id}
                            id={id}
                            studentPdfUrl={studentPdfUrl}
                            hasText
                            closeModal={closeFilesModal}
                          />

                          <StudentWordInput
                            currentICLId={icl.id}
                            id={id}
                            studentWordUrl={studentWordUrl}
                            hasText
                            closeModal={closeFilesModal}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
                {userTypeSelector === 'Student' && (
                  <div className="hidden items-center !gap-3 lg:flex">
                    {hasPhoto && (
                      <Tooltip
                        title={t('add photo')}
                        componentsProps={{
                          popper: {
                            sx: {
                              ['& .MuiTooltip-arrow']: {
                                color: '#9CDFFC',
                              },
                              ['& .MuiTooltip-tooltip']: {
                                backgroundColor: '#9CDFFC',
                                color: '#000000',
                                fontSize: '14px',
                                padding: '14px',
                                borderRadius: '8px',
                              },
                            },
                          },
                        }}
                        arrow
                      >
                        <button
                          className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600"
                          onClick={() => {
                            imgInputRef.current?.click();
                          }}
                        >
                          <img src={imageSvg} alt="image" />

                          <input
                            className="hidden"
                            id="imageInput"
                            ref={imgInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleAddImage}
                          />
                        </button>
                      </Tooltip>
                    )}

                    {icl && icl.type === WorkType.hw && (
                      <>
                        <StudentPdfInput currentICLId={icl.id} id={id} studentPdfUrl={studentPdfUrl} />
                        <StudentWordInput currentICLId={icl.id} id={id} studentWordUrl={studentWordUrl} />
                      </>
                    )}
                  </div>
                )}
                <div className="relative w-full lg:!w-[240px]">
                  {userTypeSelector === 'Student' && !isOpen && comments?.find(it => !it.isViewed) && (
                    <div className="absolute right-0 top-0 z-10 h-[20px] w-[20px] rounded-full bg-red-500" />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ItemActionModal
        isOpen={isDeleteImgOpen}
        close={closeDeleteImg}
        title={'deletePhoto'}
        onHandle={() => handleDeleteImage(imageForDelete)}
        isSubmitButtonDisabled={false}
        buttonTitle={'delete'}
      />

      <ItemActionModal
        isOpen={isDeleteEditImgOpen}
        close={closeDeleteEditImg}
        title={'deletePhoto'}
        onHandle={() => handleDeleteEditedImage(imageForDelete)}
        isSubmitButtonDisabled={false}
        buttonTitle={'delete'}
      />

      <CommentModal hw={hw} isOpen={isOpen} onClose={close} icl={icl} taskId={id} />
      <BigPictureModal isOpen={isBigPictureOpen} onClose={bigPictureCLose} img={bigPicture} />
      {deadline && <TaskDeadline deadline={deadline} />}
    </>
  );
}

export default memo(StudentTaskWrapper);
